import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {Button, TextField} from "aria";
import {useFormik} from "formik";
import {useEffect, useState} from "react";
import {observer} from "mobx-react";

const AVAILABLE_SCOPES = [
  {id: 'entertainment', label: 'Entertainment (Disney+)'},
  {id: 'snr', label: 'SNR'},
  {id: 'previsora', label: 'Previsora'},
  {id: 'tigogestion', label: 'Tigo Gestión'},
];

export default observer(function OAuthAPI(props) {
  const appDataStore = useAppDataStore();
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState({});
  const [showNewClient, setShowNewClient] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [newClientSecret, setNewClientSecret] = useState(null);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [sessionIp, setSessionIp] = useState('');
  const [sessionExpiration, setSessionExpiration] = useState(false);
  const [newSessionToken, setNewSessionToken] = useState(null);
  const [showSessionTokenModal, setShowSessionTokenModal] = useState(false);

  // Obtener scopes del usuario actual
  const userScopes = appDataStore.user?.scopes || [];
  const userScopesArray = Array.isArray(userScopes) ? userScopes :
    (typeof userScopes === 'string' ? userScopes.split(',').map(s => s.trim()) : []);

  // Filtrar scopes disponibles según los scopes del usuario
  const availableScopesForUser = AVAILABLE_SCOPES.filter(scope =>
    userScopesArray.includes(scope.id)
  );

  // Cargar clientes OAuth y sesiones al montar el componente
  useEffect(() => {
    loadClients();
    loadSessions();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await appDataStore.get('/oauth/clients');
      setClients(data.clients || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
      alert('Error al cargar los clientes OAuth: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      clientName: '',
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const selectedScopesList = Object.keys(selectedScopes).filter(scope => selectedScopes[scope]);

        if (selectedScopesList.length === 0) {
          alert('Por favor selecciona al menos un permiso');
          return;
        }

        const res = await appDataStore.post('/oauth/clients/create', {
          name: values.clientName,
          scopes: selectedScopesList,
        });

        // Guardar el secret para mostrar en modal
        setNewClientSecret(res.client.clientSecret);
        setShowSecretModal(true);

        formik.resetForm();
        setSelectedScopes({});
        setShowNewClient(false);
        await loadClients();
      } catch (err) {
        alert('Error al crear cliente: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleScopeChange = (scopeId) => {
    setSelectedScopes(prev => ({
      ...prev,
      [scopeId]: !prev[scopeId]
    }));
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
      return;
    }

    try {
      setLoading(true);
      await appDataStore.post('/oauth/clients/delete', {clientId});
      alert('Cliente eliminado exitosamente');
      await loadClients();
    } catch (err) {
      alert('Error al eliminar cliente: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const data = await appDataStore.get('/oauth/sessions');
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error al cargar sesiones:', err);
      alert('Error al cargar las sesiones: ' + err.message);
    }
  };

  const createSession = async () => {
    if (!sessionName.trim()) {
      alert('Por favor ingresa un nombre para la sesión');
      return;
    }

    try {
      setLoading(true);
      const sessionData = {
        name: sessionName,
        withExpiration: sessionExpiration,
        validateIp: false
      };

      // Agregar IP si se proporcionó
      if (sessionIp.trim()) {
        sessionData.ip = sessionIp;
      }

      const res = await appDataStore.post('/oauth/sessions/create', sessionData);

      setNewSessionToken(res.session.token);
      setShowSessionTokenModal(true);
      setSessionName('');
      setSessionIp('');
      setSessionExpiration(false);
      setShowNewSession(false);
      await loadSessions();
    } catch (err) {
      alert('Error al crear sesión: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const expireSession = async (sessionId) => {
    if (!window.confirm('¿Estás seguro de que deseas expirar esta sesión?')) {
      return;
    }

    try {
      setLoading(true);
      await appDataStore.post('/oauth/sessions/expire', {sessionId});
      alert('Sesión expirada exitosamente');
      await loadSessions();
    } catch (err) {
      alert('Error al expirar sesión: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Modal para mostrar el secret tras crear */}
      {showSecretModal && newClientSecret && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-3">⚠️ Guarda tu Client Secret</h3>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-sm text-yellow-800">
                Este es el único lugar donde verás el Client Secret. Guárdalo en un lugar seguro.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newClientSecret}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => copyToClipboard(newClientSecret, 'new-secret')}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                    copiedId === 'new-secret'
                      ? 'bg-green-500 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {copiedId === 'new-secret' ? '✓ Copiado' : 'Copiar'}
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setShowSecretModal(false);
                setNewClientSecret(null);
              }}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
            >
              Entendido, continuar
            </button>
          </div>
        </div>
      )}

      <div className="p-2 max-w-7xl mx-auto text-black">
        {/* Sección de Clientes Existentes */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Clientes OAuth</h2>

          {loading && clients.length === 0 ? (
            <div className="text-center py-8 text-gray-600">Cargando clientes...</div>
          ) : clients.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              No hay clientes OAuth creados aún
            </div>
          ) : (
            <div className="grid gap-4">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="border border-gray-300 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">
                        {client.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Creado: {new Date(client.createdAt).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteClient(client.id)}
                      disabled={loading}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white rounded text-sm"
                    >
                      Eliminar
                    </button>
                  </div>

                  {/* Client ID */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client ID
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={client.clientId}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white text-sm font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(client.clientId, `id-${client.id}`)}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          copiedId === `id-${client.id}`
                            ? 'bg-green-500 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                      >
                        {copiedId === `id-${client.id}` ? '✓ Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>

                  {/* Client Secret - No se muestra después de la creación */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret
                    </label>
                    <div className="bg-gray-100 border border-gray-300 rounded p-3">
                      <p className="text-sm text-gray-600">
                        🔒 El Client Secret no se puede recuperar después de la creación inicial.
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Si lo perdiste, deberás eliminar este cliente y crear uno nuevo.
                      </p>
                    </div>
                  </div>

                  {/* Scopes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permisos
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {client.scopes && client.scopes.length > 0 ? (
                        client.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                          >
                            {AVAILABLE_SCOPES.find(s => s.id === scope)?.label || scope}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500 text-sm">Sin permisos asignados</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botón para crear nuevo cliente */}
        {!showNewClient && (
          <div className="mb-8">
            <Button
              onClick={() => setShowNewClient(true)}
              isDisabled={loading}
            >
              Crear nuevo cliente OAuth
            </Button>
          </div>
        )}

        {/* Formulario para crear cliente */}
        {showNewClient && (
          <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 max-w-2xl">
            <h3 className="text-xl font-bold mb-4 text-gray-800">
              Crear nuevo cliente OAuth
            </h3>

            <div className="mb-4">
              <TextField
                name="clientName"
                label="Nombre del Cliente"
                placeholder="Ej: Mi Aplicación"
                onChange={(e) => formik.setFieldValue('clientName', e)}
                onBlur={formik.handleBlur}
                value={formik.values.clientName}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-800 mb-3">
                Selecciona los permisos que necesita:
              </label>
              {availableScopesForUser.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    No tienes permisos para crear clientes OAuth. Se requiere tener al menos un scope asignado.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableScopesForUser.map((scope) => (
                    <label
                      key={scope.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-gray-100 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopes[scope.id] || false}
                        onChange={() => handleScopeChange(scope.id)}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                      />
                      <span className="text-gray-700">{scope.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={formik.submitForm}
                isDisabled={formik.isSubmitting || loading || !formik.values.clientName}
              >
                Crear Cliente
              </Button>
              <button
                onClick={() => {
                  setShowNewClient(false);
                  formik.resetForm();
                  setSelectedScopes({});
                }}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 disabled:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Modal para mostrar el token de sesión tras crear */}
        {showSessionTokenModal && newSessionToken && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-3">🔐 Guarda tu Token de Sesión</h3>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  Este es el único lugar donde verás este token. Guárdalo en un lugar seguro.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Token</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSessionToken}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(newSessionToken, 'new-session-token')}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                      copiedId === 'new-session-token'
                        ? 'bg-green-500 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    {copiedId === 'new-session-token' ? '✓ Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowSessionTokenModal(false);
                  setNewSessionToken(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
              >
                Entendido, continuar
              </button>
            </div>
          </div>
        )}

        {/* Sección de Sesiones */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Tokens de Sesión</h2>

          {sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              No hay sesiones creadas aún
            </div>
          ) : (
            <div className="grid gap-4">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`border rounded-lg p-4 ${
                    session.active
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">
                        {session.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {session.active ? '✓ Activa' : '✗ Expirada'} •
                        Creado: {new Date(session.createdAt).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    {session.active && (
                      <button
                        onClick={() => expireSession(session.id)}
                        disabled={loading}
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white rounded text-sm"
                      >
                        Expirar
                      </button>
                    )}
                  </div>

                  {/* Token */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Token
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={session.token}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white text-sm font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(session.fullToken, `token-${session.id}`)}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          copiedId === `token-${session.id}`
                            ? 'bg-green-500 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                      >
                        {copiedId === `token-${session.id}` ? '✓ Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>

                  {/* IP, Expiration y Badges */}
                  <div className="mb-3">
                    <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                      {session.validateIp &&
                        <div>
                          <span className="font-medium">IP:</span> {session.ip || 'N/A'}
                          <span
                            className={`px-2 mx-2 py-1 ${session.validateIp ? 'bg-green-500' : 'bg-red-500'} text-white  rounded text-xs font-medium`}>
                        {session.validateIp ? 'IP Permitida' : 'no se valida IP'}
                      </span>
                        </div>
                      }
                      <div>
                        <span className="font-medium">Con expiración:</span> {session.withExpiration ? 'Sí' : 'No'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botón para crear nueva sesión */}
        {!showNewSession && (
          <div className="mb-8">
            <Button
              onClick={() => setShowNewSession(true)}
              isDisabled={loading}
            >
              Crear nuevo token de sesión
            </Button>
          </div>
        )}

        {/* Formulario para crear sesión */}
        {showNewSession && (
          <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 max-w-2xl">
            <h3 className="text-xl font-bold mb-4 text-gray-800">
              Crear nuevo token de sesión
            </h3>

            <div className="mb-4">
              <TextField
                label="Nombre de la Sesión"
                placeholder="Ej: API Token para mi aplicación"
                value={sessionName}
                onChange={(e) => setSessionName(e)}
              />
            </div>

            <div className="mb-4">
              <TextField
                label="IP (Opcional)"
                placeholder="Ej: 192.168.1.1 o dejar vacío para usar tu IP actual"
                value={sessionIp}
                onChange={(e) => setSessionIp(e)}
              />
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sessionExpiration}
                  onChange={(e) => setSessionExpiration(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                />
                <span className="text-gray-700">Expirar automáticamente después de 1 hora de inactividad</span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={createSession}
                isDisabled={loading || !sessionName.trim()}
              >
                Crear Sesión
              </Button>
              <button
                onClick={() => {
                  setShowNewSession(false);
                  setSessionName('');
                  setSessionIp('');
                  setSessionExpiration(false);
                }}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 disabled:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
});